import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminShell from './AdminShell';
import { getAdminBooking, updateBooking } from '../../api/client';

const STATUSES = ['pending', 'confirmed', 'paid', 'cancelled'];
const STATUS_LABELS = { pending: 'Ожидает', confirmed: 'Подтверждено', paid: 'Оплачено', cancelled: 'Отменено' };

export default function AdminBookingDetail() {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    getAdminBooking(id).then(b => {
      setBooking(b);
      setForm({
        status: b.status,
        payment_status: b.payment_status,
        total_price_rub: b.total_price_rub,
        payment_ref: b.payment_ref || '',
        admin_notes: b.admin_notes || '',
      });
    }).finally(() => setLoading(false));
  }, [id]);

  async function save() {
    setSaving(true);
    try {
      const updated = await updateBooking(id, form);
      setBooking(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      alert('Ошибка сохранения: ' + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <AdminShell title="..."><div className="spinner" /></AdminShell>;
  if (!booking) return <AdminShell title="Не найдено"><div className="alert alert-error">Бронирование не найдено</div></AdminShell>;

  return (
    <AdminShell title={`Бронирование ${booking.booking_ref}`}>
      <div style={{ marginBottom: 16 }}>
        <Link to="/admin/bookings" style={{ fontSize: 14, color: 'var(--gray-500)' }}>← Все бронирования</Link>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 24, alignItems: 'start' }}>

        {/* Left: info */}
        <div>
          <div className="card" style={{ padding: 28, marginBottom: 20 }}>
            <h2 style={{ fontWeight: 700, marginBottom: 20, fontSize: 18 }}>Информация о брони</h2>
            <div className="booking-detail-grid">
              {[
                ['Номер заявки', booking.booking_ref],
                ['Статус', <span className={`badge badge-${booking.status}`}>{STATUS_LABELS[booking.status]}</span>],
                ['Квартира', booking.apartment_name],
                ['Адрес', booking.address],
                ['Заезд', booking.check_in],
                ['Выезд', booking.check_out],
                ['Ночей', booking.nights],
                ['Гостей', booking.num_guests],
                ['Источник', booking.source],
                ['Создано', booking.created_at],
              ].map(([label, value]) => (
                <div className="detail-row" key={label}>
                  <span className="detail-label">{label}</span>
                  <span className="detail-value">{value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="card" style={{ padding: 28 }}>
            <h2 style={{ fontWeight: 700, marginBottom: 20, fontSize: 18 }}>Гость</h2>
            <div className="booking-detail-grid">
              {[
                ['Имя', booking.guest_name],
                ['Телефон', booking.guest_phone],
                ['Email', booking.guest_email || '—'],
                ['Telegram chat ID', booking.telegram_chat_id || '—'],
                ['Пожелания', booking.special_requests || '—'],
              ].map(([label, value]) => (
                <div className="detail-row" key={label}>
                  <span className="detail-label">{label}</span>
                  <span className="detail-value">{value}</span>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 20, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <a
                href={`tel:${booking.guest_phone}`}
                className="btn btn-secondary"
                style={{ fontSize: 14, padding: '10px 18px' }}
              >
                📞 Позвонить
              </a>
              {booking.telegram_chat_id && (
                <a
                  href={`https://t.me/${booking.telegram_chat_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-secondary"
                  style={{ fontSize: 14, padding: '10px 18px' }}
                >
                  ✈️ Telegram
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Right: actions */}
        <div className="card" style={{ padding: 24, position: 'sticky', top: 80 }}>
          <h2 style={{ fontWeight: 700, marginBottom: 20, fontSize: 18 }}>Управление</h2>

          {saved && <div className="alert alert-success">✅ Сохранено</div>}

          <div className="form-group">
            <label className="form-label">Статус брони</label>
            <select
              className="form-input"
              value={form.status}
              onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
            >
              {STATUSES.map(s => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Статус оплаты</label>
            <select
              className="form-input"
              value={form.payment_status}
              onChange={e => setForm(f => ({ ...f, payment_status: e.target.value }))}
            >
              <option value="unpaid">Не оплачено</option>
              <option value="pending">Ожидает платёж</option>
              <option value="paid">Оплачено</option>
              <option value="refunded">Возврат</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Сумма, ₽ (итоговая)</label>
            <input
              type="number"
              className="form-input"
              value={form.total_price_rub}
              onChange={e => setForm(f => ({ ...f, total_price_rub: Number(e.target.value) }))}
            />
          </div>

          <div className="form-group">
            <label className="form-label">ID платежа / СБП номер</label>
            <input
              className="form-input"
              value={form.payment_ref}
              onChange={e => setForm(f => ({ ...f, payment_ref: e.target.value }))}
              placeholder="Референс платежа..."
            />
          </div>

          <div className="form-group">
            <label className="form-label">Заметки администратора</label>
            <textarea
              className="form-input"
              value={form.admin_notes}
              onChange={e => setForm(f => ({ ...f, admin_notes: e.target.value }))}
              placeholder="Внутренние заметки..."
              rows={3}
            />
          </div>

          <button
            onClick={save}
            className="btn btn-primary btn-full"
            disabled={saving}
          >
            {saving ? <span className="spinner" style={{ width: 18, height: 18 }} /> : '💾 Сохранить'}
          </button>

          <div style={{ marginTop: 16, fontSize: 13, color: 'var(--gray-500)', lineHeight: 1.6 }}>
            При изменении статуса на "Подтверждено" или "Оплачено" — гость автоматически получит
            уведомление через n8n/Telegram.
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
